import { httpClient } from './httpClient';
import { clearUserProfileCache } from '@/contexts/UserProfileContext';
import { clearV9ChannelFilters } from '@/utils/clearV9Filters';

/** Short on purpose: the logout must not hang on a slow network. */
const LOGOUT_TIMEOUT_MS = 3000;

export interface User {
  id: string;
  email: string;
  username: string;
  name: string;
  image?: string;
  // Adicione outros campos do usuário conforme necessário
}

export interface AuthResponse {
  token: string;
  user_id: string;
}

export interface RegisterCredentials {
  email: string;
  name: string;
  password: string;
  password_confirmation: string;
  // Add any additional registration fields as needed
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

class AuthService {
  private readonly TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token';
  private readonly USER_ID_KEY = import.meta.env.VITE_USER_ID_KEY || 'user_id';

  public async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      const response = await httpClient.post<AuthResponse>(
        '/api/register',
        {
          email: credentials.email,
          name: credentials.name,
          password: credentials.password,
          password_confirmation: credentials.password_confirmation,
          // Include any additional registration fields
        },
        {
          headers: {
            'hoopers-api-key': import.meta.env.VITE_API_KEY,
          },
        }
      );
      this.setTokens(response.token, response.user_id);
      return response;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await httpClient.post<AuthResponse>('/api/login', credentials, {
        headers: {
          'hoopers-api-key': import.meta.env.VITE_API_KEY,
        },
      });
      // Clear V9 channel filters on login to ensure fresh state
      clearV9ChannelFilters();
      this.setTokens(response.token, response.user_id);
      return response;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  /**
   * Authenticate user with Google OAuth
   * @param credential Google ID token from Google OAuth response
   * @returns AuthResponse with token and user_id
   */
  public async loginWithGoogle(credential: string): Promise<AuthResponse> {
    try {
      const response = await httpClient.post<AuthResponse>(
        '/api/auth/google',
        { idToken: credential },
        {
          headers: {
            'hoopers-api-key': import.meta.env.VITE_API_KEY,
          },
        }
      );
      // Clear V9 channel filters on login to ensure fresh state
      clearV9ChannelFilters();
      this.setTokens(response.token, response.user_id);
      return response;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  /**
   * Ending the session on the server as well as in this browser (AL-1779).
   *
   * Clearing localStorage only logs the user out of the machine in front of
   * them. The JWT stayed valid on the backend for an hour and refreshable for a
   * fortnight, so a token copied before the logout — by an extension, an XSS, a
   * dump of localStorage — kept working after it.
   *
   * The call is best effort. Someone who has lost the network still expects the
   * session to end here, so a failure is logged and the local cleanup runs
   * anyway. It also runs BEFORE clearTokens: clearAuth drops the Authorization
   * header, and without it the backend has nothing to invalidate.
   */
  public async logout(options: { allDevices?: boolean } = {}): Promise<void> {
    try {
      // Clear UserProfileContext cache FIRST (synchronously) to prevent stale data
      clearUserProfileCache();

      // Clear V9 channel filters on logout to ensure fresh state on next login
      clearV9ChannelFilters();

      if (this.getAccessToken()) {
        await httpClient.post(
          options.allDevices ? '/api/logout/all-devices' : '/api/logout',
          undefined,
          { timeout: LOGOUT_TIMEOUT_MS }
        );
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      this.clearTokens();
    }
  }

  /**
   * Solicita o envio de um e-mail de redefinição de senha para o usuário.
   * @param email E-mail do usuário
   */
  public async requestPasswordReset(email: string): Promise<void> {
    await httpClient.post(
      '/api/password/forgot',
      { email },
      {
        headers: {
          'hoopers-api-key': import.meta.env.VITE_API_KEY,
        },
      }
    );
  }

    /**
   * Redefine a senha do usuário usando o token enviado por e-mail.
   * @param token Token de redefinição
   * @param email E-mail do usuário
   * @param newPassword Nova senha
   * @param confirmPassword Confirmação da nova senha
   */
  public async resetPassword(
    token: string,
    email: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<void> {
    await httpClient.post(
      '/api/password/reset',
      {
        token,
        email,
        password: newPassword,
        password_confirmation: confirmPassword,
      },
      {
        headers: {
          'hoopers-api-key': import.meta.env.VITE_API_KEY,
        },
      }
    );
  }

  public getCurrentUser(): User | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      // For simple token format (e.g., "21|V7oIb4jGy7i0aK0ShMRt0tNsvXVq5OyMnS19aCL8b578c20c")
      if (typeof token === 'string') {
        // If token is just a string without any specific format
        if (!token.includes('|')) {
          return {
            id: '1', // Default user ID
            email: 'user@example.com',
            username: 'user',
            name: 'User',
          };
        }

        // If token has the format "user_id|random_string"
        const [userId] = token.split('|');
        return {
          id: userId || '1',
          email: userId ? `${userId}@example.com` : 'user@example.com',
          username: userId ? `user_${userId}` : 'user',
          name: userId ? `User ${userId}` : 'User',
        };
      }

      // For JWT tokens (backward compatibility)
      if (typeof token === 'object' && token !== null && 'split' in token) {
        const tokenStr = token as unknown as string;
        if (tokenStr.split('.').length === 3) {
          const base64Url = tokenStr.split('.')[1];
          if (!base64Url) {
            console.error('Invalid JWT token: no payload');
            return null;
          }

          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(window.atob(base64));

          if (!payload) {
            console.error('Failed to parse JWT token payload');
            return null;
          }

          return {
            id: payload.sub || '',
            email: payload.email || '',
            username: payload.username || payload.email?.split('@')[0] || '',
            name: payload.name || payload.username || payload.email?.split('@')[0] || 'User',
            image: payload.picture || payload.image,
          };
        }
      }

      console.error('Unsupported token format:', token);
      return null;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  public isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    // For simple token format (e.g., "21|V7oIb4jGy7i0aK0ShMRt0tNsvXVq5OyMnS19aCL8b578c20c")
    if (typeof token === 'string') {
      if (token.includes('|')) {
        const [userId] = token.split('|');
        return !!userId; // Valid if we have a user ID
      }
      // If it's just a simple string without |, still consider it valid
      return true;
    }

    // For JWT tokens (backward compatibility)
    if (typeof token === 'object' && token !== null && 'split' in token) {
      const tokenStr = token as unknown as string;
      const parts = tokenStr.split('.');
      return parts.length === 3; // Valid JWT has 3 parts
    }

    return false;
  }

  /**
   * Refresh the JWT using the current token.
   *
   * AL-1781: the comment that used to sit here said the backend accepts expired
   * tokens within the 14-day refresh window. It does not. `/api/auth/refresh`
   * is behind `auth:api`, and the JWT guard rejects an expired token before the
   * controller ever runs — so a refresh attempted AFTER expiry can never
   * succeed. The 14 days are the window in which a still-valid token may be
   * exchanged, not a grace period for a dead one.
   *
   * That is why the client now renews ahead of `exp` instead of waiting for a
   * 401 to announce a session that is already over.
   */
  public async refreshToken(): Promise<string> {
    const response = await httpClient.post<AuthResponse>(
      '/api/auth/refresh',
      null,
      {
        headers: {
          'hoopers-api-key': import.meta.env.VITE_API_KEY,
        },
      }
    );
    this.setTokens(response.token, response.user_id);
    return response.token;
  }

  public getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  public getUserId(): string | null {
    return localStorage.getItem(this.USER_ID_KEY);
  }

  /**
   * Stores a session, refusing anything that is not one (AL-1781).
   *
   * localStorage.setItem coerces its value to a string, so a response with no
   * token used to be stored as the five characters "undefined" — and
   * isAuthenticated() accepts any non-empty string, so the app then believed it
   * was signed in with a token the server had never issued. Every request after
   * that failed, two layers away from the cause.
   *
   * Throwing leaves the previous token in place, which may still have minutes
   * left on it. That is strictly better than replacing it with nonsense.
   */
  private setTokens(token: string, user_id: string): void {
    if (typeof token !== 'string' || token.trim() === '') {
      throw new Error('Authentication response carried no usable token');
    }

    localStorage.setItem(this.TOKEN_KEY, token);

    if (typeof user_id === 'string' && user_id.trim() !== '') {
      localStorage.setItem(this.USER_ID_KEY, user_id);
    }

    // Atualiza o token no cliente HTTP
    httpClient.setAuthToken(token);
  }

  private clearTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_ID_KEY);

    // Remove o token do cliente HTTP
    httpClient.clearAuth();
  }
}

export const authService = new AuthService();
